// Transaction detail (SM-755): fingerprint, amount, balance before →
// after, status timestamps, failure code, the linked original / Rollback,
// every attempt that named it, and its audit. The stored response body
// stays server-side.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AttemptsTable } from "@/components/domain/AttemptsTable";
import { AuditTable } from "@/components/domain/AuditTable";
import { StatusBadge } from "@/components/ui/Badge";
import { SectionCard } from "@/components/ui/Card";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { CopyButton, KV, Mono, PageHeader } from "@/components/ui/Misc";
import { Skeleton } from "@/components/ui/Skeleton";
import { fmtTime } from "@/lib/format";
import { fetchers, keys } from "@/lib/queries";
import type { Transaction } from "@/lib/types";

export const Route = createFileRoute("/_app/transactions/$id")({
  component: TransactionDetailPage,
});

function TxnLink({ id }: { id: string | null | undefined }) {
  if (!id) return <span className="t-mute">—</span>;
  return (
    <Link to="/transactions/$id" params={{ id }} className="mono text-[var(--neon)] hover:underline">
      {id}
    </Link>
  );
}

function TransactionDetailPage() {
  const { id } = Route.useParams();
  const q = useQuery({ queryKey: keys.transaction(id), queryFn: () => fetchers.transaction(id) });
  if (q.isLoading) return <Skeleton lines={10} />;
  if (q.error || !q.data) return <ErrorBanner error={q.error ?? new Error("Transaction not found.")} />;
  const { transaction: t, original, rollback } = q.data;
  const attempts = q.data.attempts ?? [];
  const audit = q.data.audit ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title={`${t.operation} ${t.amount}`}
        subtitle={
          <Link to="/transactions" className="t-mute hover:underline">
            ← All transactions
          </Link>
        }
        actions={<StatusBadge status={t.status} />}
      />

      <div className="card">
        <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-t-3">Transaction id</div>
        <div className="flex flex-wrap items-center gap-2">
          <Mono className="text-base font-semibold text-t-1">{t.transaction_id}</Mono>
          <CopyButton value={t.transaction_id} label="Copy id" />
        </div>
        <div className="mt-4">
          <KV
            items={[
              ["Operation", t.operation],
              ["Status", <StatusBadge status={t.status} />],
              ["Amount", <span className="mono text-base font-semibold">{t.amount}</span>],
              [
                "Balance before → after",
                <span className="mono text-base font-semibold" data-testid="balance-before-after">
                  {t.balance_before ?? "—"} → {t.balance_after ?? "—"}
                </span>,
              ],
              ["Reason", <Mono>{t.reason}</Mono>],
              [
                "User",
                <Link to="/users/$id" params={{ id: t.user_id }} className="mono hover:underline">
                  {t.user_id}
                </Link>,
              ],
              ["Client", <Mono>{t.client_id}</Mono>],
              ["Retryable", t.retryable ? "yes" : "no"],
              ["Failure code", <Mono>{t.failure_code ?? "—"}</Mono>],
              ["Response status", t.response_status],
              ["First request id", <Mono>{t.request_id}</Mono>],
              ["Fault rule", <Mono>{t.fault_rule_id ?? "—"}</Mono>],
              ["Scenario epoch", t.epoch],
            ]}
          />
        </div>
        <div className="mt-4">
          <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-t-3">Request fingerprint</div>
          <div className="flex flex-wrap items-center gap-2">
            <Mono className="text-xs text-t-1">{t.request_fingerprint}</Mono>
            <CopyButton value={t.request_fingerprint} label="Copy" />
          </div>
          <p className="mt-1 text-2xs text-t-3">
            Operation, client, user, amount, reason and original. An exact retry returns the stored answer; a changed
            fingerprint is 409 TRANSACTION_CONFLICT.
          </p>
        </div>
      </div>

      <SectionCard title="Status timeline">
        <KV
          items={[
            ["Created (client)", fmtTime(t.created_at)],
            ["Received", fmtTime(t.received_at)],
            ["Committed", fmtTime(t.committed_at)],
            ["Failed", fmtTime(t.failed_at)],
            ["Rolled back", fmtTime(t.rolled_back_at)],
            ["Pending until", fmtTime(t.pending_until ?? null)],
          ]}
        />
      </SectionCard>

      <SectionCard title="Linked transactions">
        <div className="grid gap-4 md:grid-cols-2">
          <Linked label="Original (this Rollback compensates)" id={t.original_transaction_id} txn={original} />
          <Linked label="Rollback (compensates this transaction)" id={t.rollback_transaction_id} txn={rollback} />
        </div>
      </SectionCard>

      <SectionCard title={`Attempts (${attempts.length})`}>
        <AttemptsTable rows={attempts} emptyText="No request-log attempts name this transaction." />
      </SectionCard>

      <SectionCard title="Audit">
        <AuditTable rows={audit} emptyText="No operator audit events for this transaction." />
      </SectionCard>
    </div>
  );
}

function Linked({ label, id, txn }: { label: string; id: string | null; txn?: Transaction }) {
  return (
    <div className="rounded-md border border-line-1 p-3">
      <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-t-3">{label}</div>
      {id ? (
        <>
          <TxnLink id={id} />
          {txn && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-t-2">
              <span>{txn.operation}</span>
              <span className="mono">{txn.amount}</span>
              <StatusBadge status={txn.status} />
              <span className="t-mute">{fmtTime(txn.received_at)}</span>
            </div>
          )}
        </>
      ) : (
        <span className="t-mute">None</span>
      )}
    </div>
  );
}
